import React, { useContext, useEffect } from "react";
import _ from "lodash";
import { commonInputGroupStyles } from "..";
import { TranslationContext } from "../../../../umbilical_tools";
import { createUseStyles } from "react-jss";
import { useField, Field } from "formik";
import PropTypes from "prop-types";
import Select from "react-select";
import { SelectPicker } from "rsuite";
import jssVariables from "../../../../jss_setup";

function FieldValidator({ touched, errors, name, errorMsgStyles }) {
  return touched[name] && errors[name] && <div className={errorMsgStyles}>{errors[name]}</div>;
}
FieldValidator.propTypes = {};

const selectFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  requiredLabel: (props) => ({
    fontWeight: "normal",
    ..._.get(props, "customStyles.requiredLabel", {}),
  }),
  input: (props) => ({
    border: `1px solid ${jssVariables.colors.border}`,
    borderRadius: "5px",
    height: `calc(${jssVariables.normal_gap} * 4)`,
    paddingLeft: `calc(${jssVariables.normal_gap} * 1)`,
    background: "white",
    width: "100%",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.input", {}),
  }),
  option: (props) => ({
    ..._.get(props, "customStyles.option", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      ..._.get(props, "customStyles.inputGroup.smallscreen", {}),
    }),
    input: (props) => ({
      height: `calc(${jssVariables.normal_gap} * 4)`,
      ..._.get(props, "customStyles.input.smallscreen", {}),
    }),
  },
});
export function SelectField({
  id,
  name,
  label,
  placeholder,
  options,
  optionGroups,
  customStyles,
  isRequired,
}) {
  const classes = selectFieldStyles({ customStyles });

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  const [meta] = useField({ name });

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>
        {label}
        {isRequired && <span className={classes.requiredLabel}>{t("required")}</span>}
      </label>
      <Field as="select" name={name} className={classes.input}>
        <option value="">{placeholder}</option>
        {_.isEmpty(optionGroups)
          ? options.map((option, idx) => (
              <option key={idx} value={option[0]}>
                {option[1]}
              </option>
            ))
          : _.sortBy(Object.entries(optionGroups), (v) => v[0]).map(
              ([groupLabel, options], idx) => {
                return (
                  <optgroup label={`${_.startCase(groupLabel)} (${options.length})`} key={idx}>
                    {options.map((option, idx) => (
                      <option key={idx} value={option[0]}>
                        {option[1]}
                      </option>
                    ))}
                  </optgroup>
                );
              },
            )}
      </Field>
      {meta.error ? <small className={classes.errorMessage}>{meta.error}</small> : null}
    </div>
  );
}
SelectField.defaultProps = {
  as: "select",
  label: "",
  placeholder: "",
  disabled: false,
  isRequired: false,
  optionGroups: {},
  options: [],
  id: "",
};
SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  options: PropTypes.array,
  optionGroups: PropTypes.object,
  isRequired: PropTypes.bool,
  customStyles: PropTypes.object,
  id: PropTypes.string,
};

function customStyles(props) {
  return {
    control: (base) => ({
      ...base,
      border: `1px solid ${jssVariables.colors.text.dark}`,
      borderRadius: "5px",
      minHeight: "3rem",
      display: "grid",
      gridTemplateColumns: "1fr max-content",
      background: "white",
      ..._.get(props, "customStyles.reactSelectControl", {}),
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "medium",
      color: jssVariables.colors.text.dark,
      fontFamily: jssVariables.fontFamily,
      ..._.get(props, "customStyles.singleValue", {}),
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "12rem", // Height of the options popup menu
    }),
  };
}
const reactSelectFieldComponentStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    "& > div": {
      // color: jssVariables.colors.text.dark,
      borderColor: jssVariables.colors.border,
    },
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    inputGroup: (props) => ({
      ..._.get(props, "customStyles.inputGroup.smallscreen", {}),
    }),
  },
});
const ReactSelectFieldComponent = ({ field, form: { setFieldValue }, ...props }) => {
  const classes = reactSelectFieldComponentStyles();

  function onUpdate(data) {
    if (props.isMulti) {
      setFieldValue(field.name, data);
    } else {
      setFieldValue(field.name, data);
    }
  }
  useEffect(() => {
    if (props.options.length === 1 && props.preselectIfOnlyOne) {
      onUpdate(props.options[0]);
    }
  }, []);

  return (
    <Select
      className={classes.inputGroup}
      {...props}
      styles={customStyles({ customStyles: props.customStyles })}
      value={field.value}
      onChange={(data) => onUpdate(data)}
    />
  );
};
ReactSelectFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  closeMenuOnSelect: PropTypes.bool,
  customStyles: PropTypes.object,
  preselectIfOnlyOne: PropTypes.bool,
};
const reactSelectFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    alignContent: "baseline",
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  placeholder: (props) => ({
    fontSize: "medium",
    ..._.get(props, "customStyles.placeholder", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  reactSelectStyles: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    height: "5rem",
    border: `1px solid ${jssVariables.colors.text.dark}`,
    ..._.get(props, "customStyles.reactSelectStyles", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
});

export const ReactSelectField = ({
  id,
  name,
  label,
  options,
  isMulti,
  placeholder,
  closeMenuOnSelect,
  isDisabled,
  customStyles,
  preselectIfOnlyOne,
}) => {
  const classes = reactSelectFieldStyles({ isDisabled, customStyles });

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>{label}</label>
      <Field
        name={name}
        options={options}
        isMulti={isMulti}
        placeholder={<div className={classes.placeholder}>{placeholder}</div>}
        closeMenuOnSelect={closeMenuOnSelect}
        component={ReactSelectFieldComponent}
        preselectIfOnlyOne={preselectIfOnlyOne}
        isDisabled={isDisabled}
        customStyles={customStyles}
      />
    </div>
  );
};
ReactSelectField.defaultProps = {
  name: "",
  label: "",
  options: [],
  isMulti: false,
  closeMenuOnSelect: true,
  disabled: false,
  preselectIfOnlyOne: false,
  id: "",
};
ReactSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  isMulti: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  customStyles: PropTypes.object,
  preselectIfOnlyOne: PropTypes.bool,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  id: PropTypes.string,
};

const selectPickerFieldStyles = createUseStyles({
  inputGroup: (props) => ({
    ...commonInputGroupStyles.inputGroup,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  label: (props) => ({
    ...commonInputGroupStyles.label,
    ..._.get(props, "customStyles.label", {}),
  }),
  errorMessage: (props) => ({
    ...commonInputGroupStyles.errorMessage,
    ..._.get(props, "customStyles.errorMessage", {}),
  }),
});
const SelectPickerFieldComponent = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  ...props
}) => {
  const {
    styles: { errorMessage },
  } = props;
  return (
    <React.Fragment>
      <SelectPicker
        size="lg"
        placement="auto"
        searchable={false}
        value={field.value}
        {...props}
        onChange={function (value) {
          setFieldValue(field.name, value);
        }}
      />
      <FieldValidator
        touched={touched}
        errors={errors}
        name={field.name}
        errorMsgStyles={errorMessage}
      />
    </React.Fragment>
  );
};
SelectPickerFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  customStyles: PropTypes.object,
  preselectIfOnlyOne: PropTypes.bool,
};
export const SelectPickerField = ({ name, label, data }) => {
  const classes = selectPickerFieldStyles();

  return (
    <div className={classes.inputGroup}>
      <label className={classes.label}>{label}</label>
      <Field name={name} data={data} component={SelectPickerFieldComponent} />
    </div>
  );
};
SelectPickerField.defaultProps = {
  name: "",
  label: "",
  data: [],
};
SelectPickerField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  data: PropTypes.array.isRequired,
};
